/**
 * Mirror dell'enum backend Neworg.AmSuite.AuthN.Application.Models.UserTypeDisplay
 * (esposto da `MyProfileDto.UserType`). Serializzato come numero in JSON.
 *
 * NB: valori numerici diversi dall'enum di dominio
 * `Neworg.AmSuite.IdentityLink.Contracts.Models.UserType` — `LegacyMigrated`
 * del dominio è collassato a `Local` qui, discriminato dal flag `isMigrated`.
 */
export enum UserType {
  Legacy = 0,
  Local = 1,
  Federated = 2,
  NotProvisioned = 3
}

/**
 * Raw API response from AuthNService GET /v1/me/profile
 * Represents the exact structure returned by the backend.
 *
 * NB: al momento il BE non espone hasLegacyAccount/hasB2CAccount —
 * adaptMyProfile fa fallback su accountEnabled per derivare hasAccount.
 */
export interface ApiMyProfile {
  readonly objectId: string;
  readonly displayName: string | null;
  readonly givenName: string | null;
  readonly surname: string | null;
  readonly email: string | null;
  readonly accountEnabled: boolean;
  readonly partyId: string | null;
  readonly isMigrated: boolean;
  readonly createdDateTime: string | null;
  readonly userType: UserType | null;
  // Campi opzionali — presenti quando BE espone i flag su MyProfileDto
  readonly hasLegacyAccount?: boolean;
  readonly hasB2CAccount?: boolean;
}

/**
 * Application domain model for user profile.
 * Used throughout the application for profile display.
 *
 * `hasAccount` è la fonte canonica per "ha un account" — derivato in adaptMyProfile.
 */
export interface MyProfile {
  readonly objectId: string;
  readonly displayName: string | null;
  readonly givenName: string | null;
  readonly surname: string | null;
  readonly email: string | null;
  readonly partyId: string | null;
  readonly isMigrated: boolean;
  readonly createdDateTime: string | null;
  readonly userType: UserType | null;
  readonly hasLegacyAccount: boolean;
  readonly hasB2CAccount: boolean;
  /** Derivato. Fallback su accountEnabled finché BE non espone i flag su MyProfileDto. */
  readonly hasAccount: boolean;

  /**
   * @deprecated Letto solo come fallback in adaptMyProfile finché BE non aggiorna
   * MyProfileDto. Non leggerlo direttamente nei componenti — usa `hasAccount`.
   */
  readonly accountEnabled: boolean;
}

/**
 * Adapter function to transform raw API profile response into application domain model.
 * Computes hasAccount with fallback on accountEnabled.
 */
export function adaptMyProfile(apiData: ApiMyProfile): MyProfile {
  const hasLegacyAccount = apiData.hasLegacyAccount ?? false;
  const hasB2CAccount = apiData.hasB2CAccount ?? false;
  return {
    objectId: apiData.objectId,
    displayName: apiData.displayName,
    givenName: apiData.givenName,
    surname: apiData.surname,
    email: apiData.email,
    accountEnabled: apiData.accountEnabled,
    partyId: apiData.partyId,
    isMigrated: apiData.isMigrated,
    createdDateTime: apiData.createdDateTime,
    userType: apiData.userType ?? null,
    hasLegacyAccount,
    hasB2CAccount,
    // Fallback: BE attualmente non manda hasLegacyAccount/hasB2CAccount per /me/profile.
    // Quando BE aggiornerà MyProfileDto, i flag primari vincono nell'order-of-precedence.
    hasAccount: hasLegacyAccount || hasB2CAccount || apiData.accountEnabled
  };
}

/**
 * Request body for POST /v1/me/change-email (AuthN).
 */
export interface ChangeEmailRequest {
  readonly newEmail: string;
}

/**
 * Response body for POST /v1/me/change-email (AuthN).
 * `userType` mirror dell'enum BE (Legacy=0, Local=1, Federated=2, NotProvisioned=3).
 * I flag soft-fail sono presenti solo se almeno uno è true (response 207 MultiStatus).
 */
export interface ChangeEmailResult {
  readonly newEmail: string;
  readonly partyId: string | null;
  readonly userType: UserType;
  readonly isMigrated: boolean;
  readonly ilSyncFailed?: boolean;
  readonly crmSyncFailed?: boolean;
  readonly authZSyncFailed?: boolean;
}
